<template>
  <div class="content">
    <div class="modal-header border-0 custom-modal-header">
      <div class="page-title">
        <h4>{{ isEditMode ? 'Edit Expense' : 'Add Expense' }}</h4>
      </div>
      <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close">
        <span aria-hidden="true">×</span>
      </button>
    </div>
    <div class="modal-body custom-modal-body">
      <form @submit.prevent="saveExpense">
        <div class="row">
          <div class="col-lg-6">
            <div class="mb-3">
              <label for="category" class="form-label">Category</label>
              <select
                v-model="form.category_id"
                id="category"
                class="form-control"
                :class="{ 'is-invalid': errors.category_id }"
              >
                <option value="">Select Category</option>
                <option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
              <div v-if="errors.category_id" class="invalid-feedback">
                {{ errors.category_id[0] }}
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="mb-3">
              <label for="expense_date" class="form-label">Date</label>
              <input
                v-model="form.expense_date"
                type="date"
                id="expense_date"
                class="form-control"
                :class="{ 'is-invalid': errors.expense_date }"
              >
              <div v-if="errors.expense_date" class="invalid-feedback">
                {{ errors.expense_date[0] }}
              </div>
            </div>
          </div>

          <div class="col-lg-12">
            <div class="mb-3">
              <label for="amount" class="form-label">Amount</label>
              <input
                v-model.number="form.amount"
                type="number"
                step="0.01"
                id="amount"
                class="form-control"
                :class="{ 'is-invalid': errors.amount }"
                placeholder="Enter amount"
              >
              <div v-if="errors.amount" class="invalid-feedback">
                {{ errors.amount[0] }}
              </div>
            </div>
          </div>

          <div class="mb-4">
            <label for="description" class="form-label">Description</label>
            <textarea
              v-model="form.description"
              id="description"
              rows="3"
              class="form-control"
              :class="{ 'is-invalid': errors.description }"
              placeholder="Enter description"
            ></textarea>
            <div v-if="errors.description" class="invalid-feedback">
              {{ errors.description[0] }}
            </div>
          </div>
        </div>

        <div class="modal-footer-btn">
          <a href="javascript:void(0);" class="btn btn-cancel me-2" data-bs-dismiss="modal">
            Cancel
          </a>
          <button type="submit" class="btn btn-submit" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            {{ isEditMode ? 'Update' : 'Submit' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'AdminExpenseCreate',

  props: {
    expenseId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      form: {
        category_id: '',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        description: ''
      },
      categories: [],
      errors: {},
      loading: false,
      isEditMode: false,
      initialLoading: true
    };
  },

  async mounted() {
    console.log('AdminExpenseCreate component mounted');
    console.log('Expense ID:', this.expenseId);

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');

    await this.loadCategories();

    if (this.expenseId) {
      await this.loadExpense();
    }

    this.initialLoading = false;
  },

  methods: {
    async loadCategories() {
      try {
        const response = await this.$http.get('/api/expenses/categories');

        if (response.data.success) {
          this.categories = response.data.categories;
        } else {
          this.showErrorMessage('Failed to load expense categories');
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
        this.showErrorMessage('Failed to load expense categories');
      }
    },

    async loadExpense() {
      try {
        const response = await this.$http.get(`/api/expenses/${this.expenseId}`);

        if (response.data.success) {
          const expense = response.data.expense;
          this.form = {
            category_id: expense.expense_category_id,
            amount: expense.amount,
            expense_date: expense.expense_date,
            description: expense.notes || ''
          };
          this.isEditMode = true;
        } else {
          this.showErrorMessage('Failed to load expense data');
        }
      } catch (error) {
        console.error('Failed to load expense:', error);
        this.showErrorMessage('Failed to load expense data');
      }
    },

    async saveExpense() {
      this.loading = true;
      this.errors = {};

      try {
        let response;

        if (this.isEditMode) {
          response = await this.$http.put(`/api/expenses/admin/${this.expenseId}`, this.form);
        } else {
          response = await this.$http.post('/api/expenses/admin', this.form);
        }

        if (response.data.success) {
          this.showSuccessMessage(response.data.message);

          // Close modal and refresh page
          setTimeout(() => {
            const modal = bootstrap.Modal.getInstance(document.getElementById('expenseModal'));
            if (modal) {
              modal.hide();
            }
            window.location.reload();
          }, 1500);
        } else {
          this.showErrorMessage(response.data.message || 'Failed to save expense');
        }

      } catch (error) {
        console.error('Admin expense save failed:', error);

        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showErrorMessage('Please check the form for errors');
        } else if (error.response?.status === 403) {
          this.showErrorMessage('Unauthorized action. Admin access required.');
        } else {
          this.showErrorMessage(
            error.response?.data?.message || 'An error occurred while saving the expense'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        category_id: '',
        amount: '',
        expense_date: new Date().toISOString().split('T')[0],
        description: ''
      };
      this.errors = {};
      this.isEditMode = false;
    },

    editExpense(expenseId) {
      this.expenseId = expenseId;
      this.loadExpense();
    },

    showSuccessMessage,
    showErrorMessage
  },

  watch: {
    expenseId(newId) {
      if (newId) {
        this.loadExpense();
      } else {
        this.resetForm();
      }
    }
  }
};
</script>

<style scoped>
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.vue-component-loading {
  display: none;
}
</style>
